/**
 * The phone CHANGE PASSWORD sheet — `design/GT Mobile Crew.dc.html` (the
 * `pwShow` overlay), DESIGN-CHANGELOG 2026-08-18 "Mobile account page: CHANGE
 * PASSWORD is a sheet".
 *
 * The three password fields used to sit open on the Profile page as the
 * SECURITY panel; on the 430px column they live behind a 52px row now. The
 * body is the old panel's contents UNCHANGED — same store fields (`pfCur` /
 * `pfN1` / `pfN2`), same `savePw()` with the same rules and toasts — so no
 * auth logic moved, only where the form paints. Desktop keeps its inline
 * panel: it has the column width for it.
 *
 * Typed state does not survive the sheet: `openPw` and `closePw` both clear
 * the three fields, and a successful `savePw()` closes the sheet itself.
 * Registered in `store/history.ts` (kind `pw`) so the back gesture closes it
 * and `lib/scroll.ts` freezes the page beneath it.
 */

import type { CSSProperties } from 'react';

import { PixelButton } from '../components/PixelButton.tsx';
import { Sheet } from '../components/Sheet.tsx';
import { useQd } from '../store/index.ts';
import { profileVM } from '../store/selectors.ts';

const FIELD: CSSProperties = {
  background: 'var(--gt-field)',
  border: '1px solid var(--gt-border-2)',
  color: 'var(--gt-text)',
  fontFamily: 'var(--gt-font-mono)',
  fontSize: '12px',
  padding: '12px',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};

const LABEL: CSSProperties = {
  fontFamily: 'var(--gt-font-mono)',
  fontSize: '9px',
  color: 'var(--gt-muted-2)',
};

const GROUP: CSSProperties = { display: 'flex', flexDirection: 'column', gap: '6px' };

export default function PasswordSheet(): JSX.Element {
  const s = useQd();
  const v = profileVM(s);

  return (
    <Sheet
      label="Change password sheet"
      maxHeight="90vh"
      onClose={s.closePw}
      header={{ title: 'CHANGE PASSWORD', titleClr: 'var(--gt-text)', titleFs: '8.5px' }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '13px', padding: '15px 14px 18px' }}>
        {v.hasPwNote ? (
          <div style={{ fontFamily: 'var(--gt-font-mono)', fontSize: '10px', color: 'var(--acc2)' }}>
            <span>{v.pwNote}</span>
          </div>
        ) : null}
        {v.showCur ? (
          <div style={GROUP}>
            <span style={LABEL}>CURRENT PASSWORD</span>
            <input
              type="password"
              value={v.pfCur}
              onChange={(e) => s.setProfileForm({ pfCur: e.target.value })}
              style={FIELD}
            />
          </div>
        ) : null}
        <div style={GROUP}>
          <span style={LABEL}>NEW PASSWORD</span>
          <input
            type="password"
            value={v.pfN1}
            onChange={(e) => s.setProfileForm({ pfN1: e.target.value })}
            style={FIELD}
          />
        </div>
        <div style={GROUP}>
          <span style={LABEL}>CONFIRM NEW PASSWORD</span>
          <input
            type="password"
            value={v.pfN2}
            onChange={(e) => s.setProfileForm({ pfN2: e.target.value })}
            style={FIELD}
          />
        </div>
        <PixelButton
          label={v.pwLbl}
          busy={v.pwBusy}
          onClick={() => void s.savePw()}
          fs={8}
          pad="14px 14px"
          center
        />
      </div>
    </Sheet>
  );
}
